import { useLogsTemplateQuery } from '@/__generated__/graphql';
import { useEffect, useMemo, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  selectedWorklogsTemplateId,
  worklogsTemplateFields,
  selectedWorklogsTemplateField,
  selectedWorklogsTemplate,
} from '../state';
import { PageLoader } from '@/components/shared/PageLoader';
import { WorklogsFieldsPanel } from './WorklogsFieldsPanel';
import { KpiLogFormConfig } from './KPILogForm/KpiLogFormConfig';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { workpackTabStyles, FloatingLine } from './styles';
import { DerivedFieldsForm } from './KPIDerivedFieldsForm/DerivedFieldsForm';
import { Box } from '@mui/material';

type WorklogsTemplateFormProps = {
  structureId: string;
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
};

export const WorklogsTemplateForm = ({
  structureId,
  emptyForm,
  handleEmptyForm,
}: WorklogsTemplateFormProps) => {
  const worklogsTemplateId = useAtomValue(selectedWorklogsTemplateId);
  const setWorklogsTemplate = useSetAtom(selectedWorklogsTemplate);
  const setWorklogsTemplateFields = useSetAtom(worklogsTemplateFields);
  const setSelectedWorklogsTemplateField = useSetAtom(selectedWorklogsTemplateField);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const { data: worklogsTemplateData, loading } = useLogsTemplateQuery({
    variables: {
      logsTemplateId: worklogsTemplateId || '',
    },
    skip: !worklogsTemplateId,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (worklogsTemplateData?.logsTemplate) {
      setWorklogsTemplate(worklogsTemplateData?.logsTemplate);
      setWorklogsTemplateFields(worklogsTemplateData?.logsTemplate?.fields);
      setSelectedWorklogsTemplateField(worklogsTemplateData?.logsTemplate?.fields.at(0));
    }
  }, [
    worklogsTemplateData,
    setWorklogsTemplate,
    setWorklogsTemplateFields,
    setSelectedWorklogsTemplateField,
  ]);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  };

  const tabs = useMemo(() => {
    return [
      {
        index: 0,
        label: 'Fields',
        component: (
          <WorklogsFieldsPanel
            emptyForm={emptyForm}
            handleEmptyForm={handleEmptyForm}
            structureId={structureId}
          />
        ),
      },
      {
        index: 1,
        label: 'KPI Configuration',
        component: <KpiLogFormConfig structureId={structureId} />,
      },
      {
        index: 2,
        label: 'KPI Derived Fields',
        component: <DerivedFieldsForm structureId={structureId} />,
      },
    ];
  }, [structureId, emptyForm, handleEmptyForm]);

  if (loading) return <PageLoader />;

  return (
    <Box sx={{ pt: 3.5 }}>
      <BasicTabs
        tabs={tabs}
        selectedTab={selectedTab}
        changeTab={handleTabChange}
        tabsStyles={workpackTabStyles}
        customSeperator={<FloatingLine />}
        renderingStrategy="eager"
      />
    </Box>
  );
};
